import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { VehicleProvider } from './contexts/VehicleContext';
import { AuthProvider } from './contexts/AuthContext';

// Import components
import LoginPage from './components/auth/LoginPage';
import AdminDashboard from './components/dashboards/AdminDashboard';
import EmployeeDashboard from './components/dashboards/EmployeeDashboard';
import CustomerDashboard from './components/dashboards/CustomerDashboard';
import VehicleManagement from './components/vehicles/VehicleManagement';
import OrderManagement from './components/orders/OrderManagement';
import CustomerInquiries from './components/inquiries/CustomerInquiries';
import VehicleRequests from './components/requests/VehicleRequests';
import AgentCalculator from './components/calculators/AgentCalculator';
import UserManagement from './components/users/UserManagement';
import PaymentManagement from './components/payments/PaymentManagement';
import ReportsManagement from './components/reports/ReportsManagement';
import PublicMarketplace from './components/public/PublicMarketplace';
import HomePage from './components/public/HomePage';
import VehicleListPage from './components/public/VehicleListPage';
import VehicleDetailPage from './components/public/VehicleDetailPage';
import ContactPage from './components/public/ContactPage';
import AboutPage from './components/public/AboutPage';
import ShippingPage from './components/public/ShippingPage';
import HowItWorksPage from './components/public/HowItWorksPage';
import BlogPage from './components/public/BlogPage';
import BlogPostPage from './components/public/BlogPostPage';
import ComparePage from './components/public/ComparePage';
import FavoritesPage from './components/public/FavoritesPage';
import TermsPage from './components/public/TermsPage';
import PrivacyPage from './components/public/PrivacyPage';
import NotFoundPage from './components/public/NotFoundPage';
import ArrivalSchedulePage from './components/public/ArrivalSchedulePage';
import ShippedVehicles from './components/public/ShippedVehicles';
import ShippedCountryPage from './components/public/ShippedCountryPage';

// Customer Portal
import CustomerLogin from './components/customer/LoginPage';
import CustomerRegister from './components/customer/RegisterPage';
import ForgotPassword from './components/customer/ForgotPassword';
import CustomerDashboardPage from './components/customer/CustomerDashboard';
import MyOrders from './components/customer/MyOrders';
import MyInquiries from './components/customer/MyInquiries';
import MyReservations from './components/customer/MyReservations';
import ProtectedCustomerRoute from './components/customer/ProtectedCustomerRoute';
import MyOrdersCRM from './components/orders/MyOrdersCRM';
import EmployeeCommission from './components/commissions/EmployeeCommission';
import CompanySettings from './components/settings/CompanySettings';
import BlogManagement from './components/blog/BlogManagement';
import ShippingRules from './components/admin/ShippingRules';
import ArrivalSchedule from './components/admin/ArrivalSchedule';
import Layout from './components/layout/Layout';
import { API } from './config';

// Axios configuration
axios.interceptors.request.use(
  (config) => {
    // Check if this is a customer API call
    const isCustomerAPI = config.url?.includes('/public/customer/');
    
    // Use appropriate token based on API type
    const token = isCustomerAPI 
      ? localStorage.getItem('customerToken')
      : localStorage.getItem('token');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only redirect to CRM login if it's a CRM API call (not customer portal)
      const isCustomerAPI = error.config?.url?.includes('/public/customer/');
      // Don't redirect for receipt/file download endpoints - they may fail gracefully
      const isFileEndpoint = error.config?.url?.includes('/receipts') || 
                             error.config?.url?.includes('/payment-receipts') ||
                             error.config?.url?.includes('/payment-receipt-view') ||
                             error.config?.url?.includes('/tt-receipt-view') ||
                             error.config?.url?.includes('/tt-receipts') ||
                             error.config?.url?.includes('/commission-receipts');
      
      if (!isCustomerAPI && !isFileEndpoint) {
        // CRM authentication failed - redirect to CRM login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
      // For customer API or file endpoints, DON'T remove token - let component handle error
    }
    return Promise.reject(error);
  }
);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuthentication();
  }, []);

  const checkAuthentication = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (token) {
        // Always fetch fresh user data from backend to get latest permissions
        const response = await axios.get(`${API}/auth/me`);
        const freshUserData = response.data;
        
        // Update localStorage and state with fresh data
        localStorage.setItem('user', JSON.stringify(freshUserData));
        setUser(freshUserData);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);
    toast.success(`Welcome back, ${userData.full_name}!`);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  // eslint-disable-next-line react/no-unstable-nested-components
  const ProtectedRoute = ({ children, allowedRoles }) => {
    if (!isAuthenticated) {
      return <Navigate to="/login" replace />;
    }

    if (allowedRoles && !allowedRoles.includes(user?.role)) {
      toast.error('Access denied - Insufficient permissions');
      return <Navigate to="/dashboard" replace />;
    }

    return children;
  };

  const getDashboardComponent = () => {
    switch (user?.role) {
      case 'admin':
        return <AdminDashboard user={user} />;
      case 'employee':
        return <EmployeeDashboard user={user} />;
      case 'customer':
        return <CustomerDashboard user={user} />;
      default:
        return <div className="text-center py-8 text-red-600">Invalid user role</div>;
    }
  };

  return (
    <AuthProvider>
      <VehicleProvider>
        <Router>
          <div className="App">
          <Routes>
          {/* Public Routes - No authentication required */}
          <Route path="/" element={<HomePage />} />
          <Route path="/vehicles" element={<VehicleListPage />} />
          <Route path="/vehicles/:id" element={<VehicleDetailPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/shipping" element={<ShippingPage />} />
          <Route path="/arrival-schedule" element={<ArrivalSchedulePage />} />
          <Route path="/shipped" element={<ShippedVehicles />} />
          <Route path="/shipped/:country" element={<ShippedCountryPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/compare" element={<ComparePage />} />
          <Route path="/favorites" element={<FavoritesPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/marketplace" element={<PublicMarketplace />} />
          
          {/* Login Route */}
          <Route 
            path="/login" 
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <LoginPage onLogin={handleLogin} />
              )
            } 
          />
          
          {/* Dashboard Route */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Layout user={user} onLogout={handleLogout}>
                  {getDashboardComponent()}
                </Layout>
              </ProtectedRoute>
            } 
          />
          
          {/* Vehicle Management */}
          <Route 
            path="/admin/vehicles" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'employee']}>
                <Layout user={user} onLogout={handleLogout}>
                  <VehicleManagement user={user} />
                </Layout>
              </ProtectedRoute>
            } 
          />
          
          {/* Order Management */}
          <Route 
            path="/orders" 
            element={
              <ProtectedRoute>
                <Layout user={user} onLogout={handleLogout}>
                  <OrderManagement user={user} />
                </Layout>
              </ProtectedRoute>
            } 
          />
          
          {/* Customer Inquiries */}
          <Route 
            path="/inquiries" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'employee']}>
                <Layout user={user} onLogout={handleLogout}>
                  <CustomerInquiries user={user} />
                </Layout>
              </ProtectedRoute>
            } 
          />
          
          {/* Vehicle Requests */}
          <Route 
            path="/vehicle-requests" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'employee']}>
                <Layout user={user} onLogout={handleLogout}>
                  <VehicleRequests user={user} />
                </Layout>
              </ProtectedRoute>
            } 
          />
          
          {/* My Orders - CRM View */}
          <Route 
            path="/my-orders" 
            element={
              <ProtectedRoute>
                <Layout user={user} onLogout={handleLogout}>
                  <MyOrdersCRM user={user} />
                </Layout>
              </ProtectedRoute>
            } 
          />
          
          {/* Payment Management */}
          <Route 
            path="/payments" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'employee']}>
                <Layout user={user} onLogout={handleLogout}>
                  <PaymentManagement user={user} />
                </Layout>
              </ProtectedRoute>
            } 
          />
          
          {/* Reports & Analytics */}
          <Route 
            path="/reports" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'employee']}>
                <Layout user={user} onLogout={handleLogout}>
                  <ReportsManagement user={user} />
                </Layout>
              </ProtectedRoute>
            } 
          />
          
          {/* User Management */}
          <Route 
            path="/users" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout user={user} onLogout={handleLogout}>
                  <UserManagement user={user} />
                </Layout>
              </ProtectedRoute>
            } 
          />
          
          {/* Employee Commission */}
          <Route 
            path="/commissions" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'employee']}>
                <Layout user={user} onLogout={handleLogout}>
                  <EmployeeCommission user={user} />
                </Layout>
              </ProtectedRoute>
            } 
          />
          
          {/* Blog Management */}
          <Route 
            path="/blog-management" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'employee']}>
                <Layout user={user} onLogout={handleLogout}>
                  <BlogManagement user={user} />
                </Layout>
              </ProtectedRoute>
            } 
          />

          {/* Company Settings */}
          <Route 
            path="/settings/company" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout user={user} onLogout={handleLogout}>
                  <CompanySettings />
                </Layout>
              </ProtectedRoute>
            } 
          />

          {/* Shipping Rules */}
          <Route 
            path="/shipping-rules" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout user={user} onLogout={handleLogout}>
                  <ShippingRules user={user} />
                </Layout>
              </ProtectedRoute>
            } 
          />
          <Route
            path="/agent-calculator"
            element={
              <ProtectedRoute allowedRoles={['admin','employee','agent']}>
                <Layout user={user} onLogout={handleLogout}>
                  <AgentCalculator />
                </Layout>
              </ProtectedRoute>
            }
          />
          
          {/* Arrival Schedule Admin */}
          <Route 
            path="/admin/arrival-schedule" 
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <Layout user={user} onLogout={handleLogout}>
                  <ArrivalSchedule user={user} />
                </Layout>
              </ProtectedRoute>
            } 
          />
          
          {/* Staff Login Route - Keep for internal access */}
          <Route path="/staff" element={<Navigate to="/login" replace />} />
          
          {/* 404 - Catch all unmatched routes */}

          {/* Customer Portal Routes */}
          <Route path="/customer/login" element={<CustomerLogin />} />
          <Route path="/customer/register" element={<CustomerRegister />} />
          <Route path="/customer/forgot-password" element={<ForgotPassword />} />
          <Route path="/customer/dashboard" element={
            <ProtectedCustomerRoute>
              <CustomerDashboardPage />
            </ProtectedCustomerRoute>
          } />
          <Route path="/customer/orders" element={
            <ProtectedCustomerRoute>
              <MyOrders />
            </ProtectedCustomerRoute>
          } />
          <Route path="/customer/inquiries" element={
            <ProtectedCustomerRoute>
              <MyInquiries />
            </ProtectedCustomerRoute>
          } />
          <Route path="/customer/reservations" element={
            <ProtectedCustomerRoute>
              <MyReservations />
            </ProtectedCustomerRoute>
          } />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        
        {/* Toast Notifications */}
        <Toaster 
          position="top-right" 
          toastOptions={{
            duration: 4000,
            style: {
              background: 'white',
              color: '#0f172a',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
            },
          }}
        />
        </div>
      </Router>
      </VehicleProvider>
    </AuthProvider>
  );
}

export default App;
